import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, BookOpen } from "lucide-react";

export function PassageList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/passages").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="space-y-6" data-testid="passages-page">
      <div>
        <div className="chip mb-2">Reading practice</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Short leveled passages</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">Read, then answer main-idea, detail, inference, and vocabulary questions. Auto-graded.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="passage-grid">
        {items.map((p) => (
          <Link key={p.id} to={`/passages/${p.id}`} data-testid={`passage-card-${p.id}`}
            className="p-5 rounded-3xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all">
            <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center gap-1"><BookOpen size={12} /> {p.level}</div>
            <div className="mt-2 font-display text-lg font-semibold text-slate-900">{p.title}</div>
            <div className="text-xs text-slate-500 mt-1">{p.question_count} questions</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function PassageSolver() {
  const { passageId } = useParams();
  const { user, refresh } = useAuth();
  const [p, setP] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/passages/${passageId}`).then(({ data }) => {
      setP(data);
      setAnswers(new Array(data.questions.length).fill(-1));
    });
  }, [passageId]);

  if (!p) return <div className="text-slate-500">Loading…</div>;

  const submit = async () => {
    try {
      const { data } = await api.post(`/passages/${passageId}/submit`, { answers });
      setResult(data);
      toast.success(`Scored ${data.score}/${data.total} — ${data.pct}%`);
      refresh();
    } catch { toast.error("Log in as a student to submit"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid={`passage-solver-${passageId}`}>
      <Link to="/passages" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"><ArrowLeft size={14} /> Back</Link>
      <div>
        <div className="chip mb-2">{p.level}</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{p.title}</h1>
      </div>
      <article className="p-6 rounded-3xl bg-white border border-slate-200 whitespace-pre-wrap leading-relaxed text-slate-800" data-testid="passage-text">
        {p.text}
      </article>
      {p.questions.map((q, i) => (
        <section key={i} className={`p-6 rounded-3xl border ${result ? (result.detail[i].correct ? "bg-teal-50 border-teal-200" : "bg-red-50 border-red-200") : "bg-white border-slate-200"}`} data-testid={`pq-${i}`}>
          <div className="text-xs text-slate-500 uppercase tracking-widest">{q.type.replace("_", " ")}</div>
          <div className="mt-1 font-medium text-slate-900">{i+1}. {q.q}</div>
          <div className="mt-3 grid gap-2">
            {q.choices.map((c, j) => {
              const sel = answers[i] === j;
              const isRight = result && j === result.detail[i].answer_idx;
              return (
                <button key={j} disabled={!!result} data-testid={`pq-${i}-${j}`}
                  onClick={() => { const a = [...answers]; a[i] = j; setAnswers(a); }}
                  className={`text-left px-4 py-3 rounded-xl border ${isRight ? "border-teal-500 bg-teal-50 text-teal-900" : sel ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-white"} disabled:opacity-70`}>
                  {c}
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {result ? (
        <div className="p-6 rounded-3xl bg-teal-50 border border-teal-200 text-center" data-testid="passage-result">
          <CheckCircle2 className="mx-auto text-teal-700" size={40} />
          <div className="mt-2 font-display text-2xl font-bold text-slate-900">{result.pct}% — {result.score} / {result.total}</div>
        </div>
      ) : (
        user?.role === "student" && (
          <button data-testid="passage-submit" onClick={submit} disabled={answers.some((a) => a < 0)}
            className="w-full py-4 rounded-full bg-teal-700 hover:bg-teal-800 disabled:bg-slate-300 text-white font-semibold">
            Submit answers
          </button>
        )
      )}
    </div>
  );
}
